import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Grid, Alert, Button, ToggleButton, ToggleButtonGroup,
    Skeleton, Avatar,
} from '@mui/material';
import {
    People as PeopleIcon,
    TrendingUp as TrendingUpIcon,
    Today as TodayIcon,
    PublicOutlined as PublicIcon,
    Photo as PhotoIcon,
    Timer as TimerIcon,
    Description as BioIcon,
    Favorite as MatchIcon,
    FavoriteBorder as KeepRateIcon,
    TouchApp as DecisionsIcon,
    Whatshot as TodayMatchIcon,
} from '@mui/icons-material';
import AdminSidebar from './components/AdminSidebar';
import KPIStatCard from './components/KPIStatCard';
import TrendChart from './components/TrendChart';
import FunnelChart from './components/FunnelChart';
import { adminApi, RegistrationFunnelStep, MatchmakingAnalytics, RecentSignup } from '../../api/adminApi';
import { adminTokens as T } from '../../theme/adminTokens';

const DRAWER_WIDTH = 260;

type Range = 7 | 30 | 90;

interface CountryRow {
    country: string;
    count: number;
}

const formatPct = (v: number) => `${(v * 100).toFixed(1)}%`;

const initialsFrom = (name?: string | null, email?: string | null) => {
    const source = (name && name.trim()) || (email ? email.split('@')[0] : '');
    if (!source) return '?';
    const parts = source.split(/[._\s-]/).filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || source[0].toUpperCase();
};

const timeAgo = (dateString: string) => {
    const ms = Date.now() - new Date(dateString).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
};

const flagEmoji = (code?: string | null) => {
    if (!code || code.length !== 2) return '';
    const cc = code.toUpperCase();
    if (!/^[A-Z]{2}$/.test(cc)) return '';
    return String.fromCodePoint(...[...cc].map(c => 0x1f1e6 + c.charCodeAt(0) - 65));
};

const AdminAnalyticsScreen: React.FC = () => {
    const [range, setRange] = useState<Range>(30);

    const [signupStats, setSignupStats] = useState<any>(null);
    const [signupStatsLoading, setSignupStatsLoading] = useState(true);

    const [trend, setTrend] = useState<Array<{ date: string; signups: number }>>([]);
    const [trendLoading, setTrendLoading] = useState(true);

    const [countries, setCountries] = useState<CountryRow[]>([]);
    const [countriesLoading, setCountriesLoading] = useState(true);

    const [funnelSteps, setFunnelSteps] = useState<RegistrationFunnelStep[]>([]);
    const [funnelSummary, setFunnelSummary] = useState<{ totalEntered: number; completed: number; completionRate: number } | null>(null);
    const [funnelLoading, setFunnelLoading] = useState(true);

    const [engagement, setEngagement] = useState<any>(null);
    const [engagementLoading, setEngagementLoading] = useState(true);

    const [matchmaking, setMatchmaking] = useState<MatchmakingAnalytics | null>(null);
    const [matchmakingLoading, setMatchmakingLoading] = useState(true);

    const [recentSignups, setRecentSignups] = useState<RecentSignup[]>([]);
    const [recentSignupsLoading, setRecentSignupsLoading] = useState(true);

    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setSignupStatsLoading(true);
                const r = await adminApi.getSignupStats();
                if (active) setSignupStats(r.data || null);
            } catch (e: any) {
                if (active) setError(e.message || 'Failed to load signup stats');
            } finally {
                if (active) setSignupStatsLoading(false);
            }
        })();
        return () => { active = false; };
    }, []);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setTrendLoading(true);
                const r = await adminApi.getSignupTrend({ days: range });
                if (!active) return;
                const arr = Array.isArray(r?.data) ? r.data : (r?.data?.trend ?? []);
                setTrend(
                    arr.map((row: any) => ({
                        date: new Date(row.date ?? row.day ?? row.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
                        signups: Number(row.signups ?? row.count ?? row.total ?? 0),
                    }))
                );
            } catch {
                if (active) setTrend([]);
            } finally {
                if (active) setTrendLoading(false);
            }
        })();
        return () => { active = false; };
    }, [range]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setCountriesLoading(true);
                const r = await adminApi.getCountryDistribution();
                if (!active) return;
                const arr = Array.isArray(r?.data) ? r.data : (r?.data?.countries ?? []);
                const normalised: CountryRow[] = arr
                    .map((c: any) => ({
                        country: c.country || c.name || c.code || 'Unknown',
                        count: Number(c.count ?? c.signups ?? c.total ?? 0),
                    }))
                    .filter((c: CountryRow) => c.count > 0)
                    .sort((a: CountryRow, b: CountryRow) => b.count - a.count);
                setCountries(normalised);
            } catch {
                if (active) setCountries([]);
            } finally {
                if (active) setCountriesLoading(false);
            }
        })();
        return () => { active = false; };
    }, []);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setFunnelLoading(true);
                const r = await adminApi.getRegistrationFunnel({ days: range });
                if (!active) return;
                setFunnelSteps(r.data.funnel || []);
                setFunnelSummary({
                    totalEntered: r.data.totalEntered,
                    completed: r.data.completed,
                    completionRate: r.data.completionRate,
                });
            } catch {
                if (active) { setFunnelSteps([]); setFunnelSummary(null); }
            } finally {
                if (active) setFunnelLoading(false);
            }
        })();
        return () => { active = false; };
    }, [range]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setEngagementLoading(true);
                const r = await adminApi.getEngagementAnalytics({ days: range, limit: 50 });
                if (active) setEngagement(r.data || null);
            } catch {
                if (active) setEngagement(null);
            } finally {
                if (active) setEngagementLoading(false);
            }
        })();
        return () => { active = false; };
    }, [range]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setMatchmakingLoading(true);
                const r = await adminApi.getMatchmakingAnalytics({ days: range });
                if (active) setMatchmaking(r.data || null);
            } catch {
                if (active) setMatchmaking(null);
            } finally {
                if (active) setMatchmakingLoading(false);
            }
        })();
        return () => { active = false; };
    }, [range]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setRecentSignupsLoading(true);
                const r = await adminApi.getRecentSignups({ limit: 15 });
                if (active) setRecentSignups(Array.isArray(r.data) ? r.data : []);
            } catch {
                if (active) setRecentSignups([]);
            } finally {
                if (active) setRecentSignupsLoading(false);
            }
        })();
        return () => { active = false; };
    }, []);

    const topCountries = useMemo(() => countries.slice(0, 8), [countries]);
    const maxCountryCount = topCountries[0]?.count ?? 0;

    const overallEngagement = engagement?.overallStats?.[0] || engagement?.overallStats || null;
    const avgPhotos = overallEngagement?.avg_photos_viewed ?? overallEngagement?.avg_photos ?? null;
    const avgTimeSec = overallEngagement?.avg_time_spent_seconds ?? overallEngagement?.avg_time ?? null;
    const bioViewRate = overallEngagement?.bio_view_rate ?? null;

    return (
        <Box sx={{ display: 'flex', bgcolor: T.color.surface.page, minHeight: '100vh' }}>
            <AdminSidebar />
            <Box component="main" sx={{
                flexGrow: 1,
                p: { xs: `${T.space[4]}px`, md: `${T.space[6]}px` },
                minWidth: 0,
                overflow: 'hidden',
            }}>
                {/* Header + range chips */}
                <Box sx={{ mb: `${T.space[6]}px`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: `${T.space[3]}px` }}>
                    <Box>
                        <Typography sx={{
                            fontSize: T.type.size['2xl'], fontWeight: T.type.weight.bold,
                            color: T.color.text.primary, lineHeight: T.type.lineHeight.tight,
                        }}>Engagement & growth</Typography>
                        <Typography sx={{ fontSize: T.type.size.md, color: T.color.text.secondary, mt: `${T.space[1]}px` }}>
                            Signup pipeline, geography, and how profiles are engaging with daily picks.
                        </Typography>
                    </Box>
                    <ToggleButtonGroup
                        value={range}
                        exclusive
                        onChange={(_, v) => v && setRange(v)}
                        size="small"
                        sx={{
                            bgcolor: T.color.surface.raised,
                            border: `1px solid ${T.color.surface.border}`,
                            borderRadius: `${T.radius.md}px`,
                            p: '2px',
                            '& .MuiToggleButton-root': {
                                border: 'none',
                                color: T.color.text.secondary,
                                fontWeight: T.type.weight.semibold,
                                fontSize: T.type.size.sm,
                                textTransform: 'none',
                                px: `${T.space[3]}px`,
                                py: `${T.space[1]}px`,
                                borderRadius: `${T.radius.sm}px !important`,
                                '&.Mui-selected': {
                                    bgcolor: T.color.brand[50],
                                    color: T.color.brand[700],
                                },
                                '&:hover': { bgcolor: T.color.surface.sunken },
                            },
                        }}
                    >
                        <ToggleButton value={7}>7 d</ToggleButton>
                        <ToggleButton value={30}>30 d</ToggleButton>
                        <ToggleButton value={90}>90 d</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {/* KPI row */}
                <Grid container spacing={3} sx={{ mb: `${T.space[5]}px` }}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Total users"
                            value={signupStats?.totalSignups}
                            icon={<PeopleIcon />}
                            tone="neutral"
                            loading={signupStatsLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="New today"
                            value={signupStats?.dailySignups}
                            icon={<TodayIcon />}
                            tone="info"
                            loading={signupStatsLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="This week"
                            value={signupStats?.weeklySignups}
                            icon={<TrendingUpIcon />}
                            tone="success"
                            loading={signupStatsLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Countries reached"
                            value={countries.length}
                            icon={<PublicIcon />}
                            tone="neutral"
                            loading={countriesLoading}
                        />
                    </Grid>
                </Grid>

                {/* Trend + funnel */}
                <Grid container spacing={3} sx={{ mb: `${T.space[5]}px` }}>
                    <Grid item xs={12} lg={7}>
                        <TrendChart
                            title={`Signups — last ${range} days`}
                            subtitle={trend.length ? `${trend.reduce((s, r) => s + r.signups, 0).toLocaleString()} new accounts` : undefined}
                            data={trend}
                            series={[{ key: 'signups', label: 'Signups' }]}
                            loading={trendLoading}
                            empty={!trendLoading && trend.length === 0}
                            height={320}
                        />
                    </Grid>
                    <Grid item xs={12} lg={5}>
                        <FunnelChart
                            title="Registration funnel"
                            subtitle={
                                funnelSummary
                                    ? `${formatPct(funnelSummary.completionRate)} of ${funnelSummary.totalEntered.toLocaleString()} signups completed onboarding`
                                    : undefined
                            }
                            steps={funnelSteps}
                            loading={funnelLoading}
                        />
                    </Grid>
                </Grid>

                {/* Matchmaking (daily picks) */}
                <Grid container spacing={3} sx={{ mb: `${T.space[5]}px` }}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label={`Matches — last ${range} days`}
                            value={matchmaking?.totalMatches}
                            icon={<MatchIcon />}
                            tone="success"
                            loading={matchmakingLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Matches today"
                            value={matchmaking?.matchesToday}
                            icon={<TodayMatchIcon />}
                            tone="info"
                            loading={matchmakingLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Keep-rate"
                            value={matchmaking?.keepRate != null ? formatPct(matchmaking.keepRate) : null}
                            icon={<KeepRateIcon />}
                            tone="neutral"
                            loading={matchmakingLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label={`Decisions — last ${range} days`}
                            value={matchmaking?.totalDecisions}
                            icon={<DecisionsIcon />}
                            tone="neutral"
                            loading={matchmakingLoading}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ mb: `${T.space[5]}px` }}>
                    <TrendChart
                        title={`Matches — last ${range} days`}
                        subtitle={
                            matchmaking
                                ? `${matchmaking.totalKeeps.toLocaleString()} keeps · ${matchmaking.totalPops.toLocaleString()} pops`
                                : undefined
                        }
                        data={(matchmaking?.trend ?? []).map(d => ({
                            ...d,
                            date: new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
                        }))}
                        series={[{ key: 'matches', label: 'Matches' }]}
                        loading={matchmakingLoading}
                        empty={!matchmakingLoading && (matchmaking?.trend?.length ?? 0) === 0}
                        height={280}
                    />
                </Box>

                {/* Country breakdown + engagement */}
                <Grid container spacing={3}>
                    <Grid item xs={12} lg={6}>
                        <Box sx={{
                            bgcolor: T.color.surface.raised,
                            border: `1px solid ${T.color.surface.border}`,
                            borderRadius: `${T.radius.lg}px`,
                            p: `${T.space[5]}px`,
                            boxShadow: T.shadow.sm,
                            height: '100%',
                        }}>
                            <Typography sx={{
                                fontSize: T.type.size.lg, fontWeight: T.type.weight.semibold,
                                color: T.color.text.primary, mb: `${T.space[4]}px`,
                            }}>
                                Where users are joining from
                            </Typography>

                            {countriesLoading ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
                                    {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rectangular" height={32} />)}
                                </Box>
                            ) : topCountries.length === 0 ? (
                                <Box sx={{ py: `${T.space[6]}px`, textAlign: 'center', color: T.color.text.tertiary, fontSize: T.type.size.sm }}>
                                    No country data yet.
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[3]}px` }}>
                                    {topCountries.map((c, i) => {
                                        const widthPct = maxCountryCount === 0 ? 0 : (c.count / maxCountryCount) * 100;
                                        const color = T.color.chart[i % T.color.chart.length];
                                        return (
                                            <Box key={c.country}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: `${T.space[1]}px` }}>
                                                    <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.medium, color: T.color.text.primary }}>
                                                        {c.country}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                                                        {c.count.toLocaleString()}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ position: 'relative', height: 8, bgcolor: T.color.surface.sunken, borderRadius: `${T.radius.pill}px`, overflow: 'hidden' }}>
                                                    <Box sx={{
                                                        position: 'absolute', inset: 0,
                                                        width: `${widthPct}%`,
                                                        bgcolor: color,
                                                        borderRadius: `${T.radius.pill}px`,
                                                        transition: `width ${T.motion.medium}`,
                                                        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                                                    }} />
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12} lg={6}>
                        <Box sx={{
                            display: 'flex', flexDirection: 'column', gap: `${T.space[3]}px`,
                            height: '100%',
                        }}>
                            <Typography sx={{
                                fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold,
                                color: T.color.text.secondary, textTransform: 'uppercase', letterSpacing: 0.4,
                            }}>
                                Profile engagement (last {range} days)
                            </Typography>
                            <KPIStatCard
                                label="Avg. photos viewed per pick"
                                value={avgPhotos != null ? Number(avgPhotos).toFixed(1) : null}
                                icon={<PhotoIcon />}
                                tone="info"
                                loading={engagementLoading}
                            />
                            <KPIStatCard
                                label="Avg. time on a profile"
                                value={avgTimeSec != null ? `${Math.round(Number(avgTimeSec))}s` : null}
                                icon={<TimerIcon />}
                                tone="neutral"
                                loading={engagementLoading}
                            />
                            <KPIStatCard
                                label="Bio view rate"
                                value={bioViewRate != null ? formatPct(Number(bioViewRate)) : null}
                                icon={<BioIcon />}
                                tone="success"
                                loading={engagementLoading}
                            />
                        </Box>
                    </Grid>
                </Grid>

                {/* Recent signups with country */}
                <Box sx={{
                    mt: `${T.space[5]}px`,
                    bgcolor: T.color.surface.raised,
                    border: `1px solid ${T.color.surface.border}`,
                    borderRadius: `${T.radius.lg}px`,
                    boxShadow: T.shadow.sm,
                    p: `${T.space[5]}px`,
                }}>
                    <Typography sx={{
                        fontSize: T.type.size.lg, fontWeight: T.type.weight.semibold,
                        color: T.color.text.primary, mb: `${T.space[4]}px`,
                    }}>
                        Recent signups
                    </Typography>

                    {recentSignupsLoading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
                            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rectangular" height={44} />)}
                        </Box>
                    ) : recentSignups.length === 0 ? (
                        <Box sx={{ py: `${T.space[6]}px`, textAlign: 'center', color: T.color.text.tertiary, fontSize: T.type.size.sm }}>
                            No recent signups.
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {recentSignups.map((u, i) => (
                                <Box key={u.id} sx={{
                                    display: 'flex', alignItems: 'center', gap: `${T.space[3]}px`,
                                    py: `${T.space[3]}px`,
                                    borderTop: i === 0 ? 'none' : `1px solid ${T.color.surface.border}`,
                                }}>
                                    <Avatar src={u.avatar_url || undefined} sx={{ width: 36, height: 36, bgcolor: T.color.brand[50], color: T.color.brand[700], fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold }}>
                                        {initialsFrom(u.full_name, u.email)}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: T.color.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {u.full_name || u.email || 'Unknown'}
                                        </Typography>
                                        {u.full_name && u.email && (
                                            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {u.email}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: `${T.space[1]}px`,
                                        px: `${T.space[2]}px`, py: '2px', flexShrink: 0,
                                        borderRadius: `${T.radius.pill}px`,
                                        bgcolor: T.color.surface.sunken, color: T.color.text.secondary,
                                        fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
                                    }}>
                                        {flagEmoji(u.country_code)} {u.country}
                                    </Box>
                                    {u.completed_onboarding === false && (
                                        <Box sx={{
                                            px: `${T.space[2]}px`, py: '2px', flexShrink: 0,
                                            borderRadius: `${T.radius.pill}px`,
                                            bgcolor: T.color.warning.soft, color: T.color.warning.text,
                                            fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
                                        }}>
                                            Incomplete
                                        </Box>
                                    )}
                                    <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary, whiteSpace: 'nowrap', flexShrink: 0, minWidth: 64, textAlign: 'right' }}>
                                        {timeAgo(u.created_at)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                {error && (
                    <Alert
                        severity="error"
                        sx={{ mt: `${T.space[4]}px`, borderRadius: `${T.radius.md}px` }}
                        action={<Button color="inherit" size="small" onClick={() => window.location.reload()}>Reload</Button>}
                    >
                        {error}
                    </Alert>
                )}
            </Box>
        </Box>
    );
};

export default AdminAnalyticsScreen;
